import { useTranslation } from '@eon/i18n';
import type { SidebarItem, SidebarSection } from '@eon/ui';
import {
  Activity,
  BarChart3,
  Building2,
  Cable,
  KeyRound,
  Layers,
  LayoutDashboard,
  Map,
  Settings2,
  User as UserIcon,
  Users,
} from 'lucide-react';
import type { Role } from '@eon/types';
import { canAccessAdminSurface, canAccessPortalSurface } from './permissions';

export interface NavConfig {
  brandKey: 'app.admin' | 'app.portal' | 'app.web';
  sections?: SidebarSection[];
  items?: SidebarItem[];
  showAdminHeader: boolean;
}

export function useNavConfig(role: Role | undefined | null): NavConfig {
  const { t } = useTranslation();

  if (canAccessAdminSurface(role)) {
    return {
      brandKey: 'app.web',
      showAdminHeader: true,
      sections: [
        {
          label: t('nav.sections.network'),
          items: [
            { to: '/', label: t('nav.dashboard'), icon: LayoutDashboard },
            { to: '/devices', label: t('nav.devices'), icon: Cable },
            { to: '/stations', label: t('nav.stations'), icon: Building2 },
            { to: '/sessions', label: t('nav.sessions'), icon: Activity },
          ],
        },
        {
          label: t('nav.sections.management'),
          items: [
            { to: '/users', label: t('nav.users'), icon: Users },
            { to: '/groups', label: t('nav.groups'), icon: Layers },
            { to: '/id-tags', label: t('nav.idTags'), icon: KeyRound },
          ],
        },
        {
          label: t('nav.sections.system'),
          items: [
            { to: '/map', label: t('nav.map'), icon: Map },
            { to: '/logs', label: t('nav.logs'), icon: BarChart3 },
            { to: '/settings', label: t('nav.settings'), icon: Settings2 },
          ],
        },
      ],
    };
  }

  if (canAccessPortalSurface(role)) {
    return {
      brandKey: 'app.portal',
      showAdminHeader: false,
      items: [
        { to: '/', label: t('nav.dashboard'), icon: LayoutDashboard },
        { to: '/devices', label: t('nav.devices'), icon: Cable },
        { to: '/stations', label: t('nav.stations'), icon: Building2 },
        { to: '/sessions', label: t('nav.sessions'), icon: Activity },
        { to: '/users', label: t('nav.users'), icon: Users },
        { to: '/profile', label: t('nav.orgProfile'), icon: UserIcon },
      ],
    };
  }

  return {
    brandKey: 'app.web',
    showAdminHeader: false,
    items: [],
  };
}

export function usePageTitleMap(role: Role | undefined | null): Record<string, string> {
  const { t } = useTranslation();
  const base: Record<string, string> = {
    '/': t('nav.dashboard'),
    '/devices': t('nav.devices'),
    '/stations': t('nav.stations'),
    '/sessions': t('nav.sessions'),
    '/users': t('nav.users'),
  };

  if (canAccessAdminSurface(role)) {
    return {
      ...base,
      '/groups': t('nav.groups'),
      '/id-tags': t('nav.idTags'),
      '/map': t('nav.map'),
      '/logs': t('nav.logs'),
      '/settings': t('nav.settings'),
    };
  }

  return {
    ...base,
    '/profile': t('nav.orgProfile'),
  };
}
